import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Cards from 'react-credit-cards';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import AppDialog from './AppDialog';
import TextField from '@material-ui/core/TextField';

import 'react-credit-cards/es/styles-compiled.css';

import MaskedInput from 'react-text-mask';

import AppButton from './AppButton';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../redux/actions/notifications';

import api from '../services/api';
import {
  getMyCreditCards,
  setCreditCardSelected,
} from '../redux/actions/creditCards';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  title: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(2),
  },
}));

const autoCorrectedDatePipe = createAutoCorrectedDatePipe('mm yy');
const DateMaskCustom = React.forwardRef((props, ref) => {
  return (
    <MaskedInput
      ref={ref}
      mask={[/\d/, /\d/, '/', /\d/, /\d/]}
      pipe={autoCorrectedDatePipe}
      {...props}
    />
  );
});

const numberMask = createNumberMask({
  prefix: '',
  thousandsSeparatorSymbol: '',
  integerLimit: 16,
});
const CreditMaskCustom = React.forwardRef((props, ref) => {
  return (
    <MaskedInput ref={ref} mask={numberMask} showMask={false} {...props} />
  );
});

const numberMaskCVV = createNumberMask({
  prefix: '',
  thousandsSeparatorSymbol: '',
  integerLimit: 3,
  allowLeadingZeroes: true,
});
const CvvMaskCustom = React.forwardRef((props, ref) => {
  return <MaskedInput ref={ref} mask={numberMaskCVV} {...props} />;
});

function onlyLetter(e) {
  const keyCode = !e.charCode ? e.which : e.charCode;
  const regex = new RegExp('^[a-zA-Z ]+$');
  const key = String.fromCharCode(keyCode);
  if (keyCode !== 8 && !regex.test(key)) {
    e.preventDefault();
    return false;
  }
}

const CreditCardDialog = (props) => {
  const classes = useStyles();
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [card, setCard] = useState({
    cvc: '',
    expiry: '',
    focus: '',
    name: '',
    number: '',
    isValid: false,
    year: '',
    month: '',
    dni: '',
  });

  const handleChange = (event) => {
    const target = event.target;
    const name = target.name;

    setCard({
      ...card,
      [name]: target.value,
    });
  };

  const save = async () => {
    setLoading(true);
    try {
      const { expiry, name, number, cvc } = card;

      let mainAddress = user.addresses.find(
        (address) => address.main === 'true',
      );
      let date = expiry.split('/');
      const newCreditCard = await api.creditCards.create({
        owner_name: name,
        identification_number: user?.company?.nit,
        payment_method: 'Credit card',
        type_document: 'CC',
        masked_number: number.replace(/\s+/g, ''),
        exp_year: `20${date[1]}`,
        exp_month: date[0],
        cvv: `${cvc}`.replace('_', ''),
        default: 'true',
        city_id: mainAddress?.city_id.toString(),
        address: mainAddress?.address,
        phone: user.phone,
        cell_phone: user.phone,
      });
      dispatch(getMyCreditCards()).then(() => {
        dispatch(setCreditCardSelected(newCreditCard.id));
        props.handleClose();
      });
    } catch (e) {
      dispatch(addNotification(e.message, 'error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCard = (type, isValid) => {
    setCard((prevState) => ({ ...prevState, isValid }));
  };

  return (
    <AppDialog maxWidth={800} {...props}>
      <DialogTitle id="alert-dialog-slide-title" className={classes.title}>
        Agregar tarjeta de credito
      </DialogTitle>
      <DialogContent>
        <Box mb={3} px={2} clone>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Box mb={2}>
                <Typography variant="body2">Numero de la tarjeta</Typography>
                <TextField
                  InputProps={{
                    inputComponent: CreditMaskCustom,
                  }}
                  autofocus={true}
                  type="tel"
                  inputmode="numeric"
                  pattern="[0-9\s]{13,19}"
                  autocomplete="cc-number"
                  maxlength="19"
                  variant="outlined"
                  className={classes.textField}
                  name="number"
                  onChange={handleChange}
                  margin="dense"
                  fullWidth
                />
              </Box>
              <Box mb={2}>
                <Typography variant="body2">
                  Nombre impreso en la tarjeta
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  margin="dense"
                  variant="outlined"
                  className={classes.textField}
                  onKeyDown={onlyLetter}
                  onChange={handleChange}
                />
              </Box>
              <Box mb={2}>
                <Typography variant="body2">Numero de documento</Typography>
                <TextField
                  fullWidth
                  name="dni"
                  margin="dense"
                  variant="outlined"
                  onChange={handleChange}
                  className={classes.textField}
                />
              </Box>
              <Box mb={2}>
                <Typography variant="body2">Fecha de expiración</Typography>
                <TextField
                  fullWidth
                  name="expiry"
                  margin="dense"
                  variant="outlined"
                  className={classes.textField}
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: DateMaskCustom,
                  }}
                />
              </Box>
              <Box mb={2}>
                <Typography variant="body2">CVV</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeHolder="CVC"
                  name="cvc"
                  margin="dense"
                  onChange={handleChange}
                  className={classes.textField}
                  InputProps={{
                    inputComponent: CvvMaskCustom,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mb={2}>
                <Cards
                  callback={handleCard}
                  cvc={card.cvc}
                  expiry={card.expiry}
                  focused={card.focus}
                  name={card.name}
                  number={card.number}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <AppButton
                round
                disableElevation
                size="large"
                color="secondary"
                variant="contained"
                onClick={save}
                loading={loading}
                {...(loading ? { disabled: true } : {})}
              >
                Guardar
              </AppButton>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </AppDialog>
  );
};

export default CreditCardDialog;
